// src/components/alertesCritiques/SuiviAlertesCritiques.jsx
// Le suivi des alertes critiques, premier onglet de leurs paramètres
// (Outils administratifs > Alertes critiques > Suivi).
//
// Ce que le panneau montre à chaque agent, l'administrateur le voit ici en
// page, avec ce que le panneau ne dit pas : depuis quand chaque anomalie
// attend et qui l'a saisie, ce qui a été réglé sur 30 jours et par qui, et la
// façon dont les agents répondent au panneau (affichages, rappels repoussés,
// corrections lancées, constats).
//
// Depuis le 16/10/2026, un agent peut garder ouvert le panneau « Alertes » à
// droite (bouton de la barre du haut) : le panneau central est alors
// suspendu. Les affichages et le dernier affichage ne comptent que le panneau
// central ; les clics « Corriger » comptent aussi ceux du panneau de droite
// (une fois par ligne et par ouverture). Ni l'ouverture ni la fermeture du
// panneau de droite ne sont journalisées.
//
// Qui a corrigé (lot du 16/10/2026) : une alerte des réductions ou des frais
// se corrige par une demande au Centre de Support ; le serveur crédite le
// DEMANDEUR de la demande approuvée. D'où, dans les relances, « Clics
// Corriger » (des clics, pas des corrections) à côté de « Alertes réglées »
// (des corrections), et, dans « Résolues récemment », « Par » qui nomme aussi
// l'auteur d'une correction. Un bandeau dit quand le suivi a commencé : ce qui
// a été corrigé avant n'y figure pas (date lue en base, montrée à l'heure de
// Paris).
//
// Le serveur calcule tout (GET /api/parametres/alertes/suivi) : compteurs,
// ancienneté, durées et jours de l'avancement, en SQL. La page met en forme
// et n'écrit rien : pas de constat d'ici, seuls « Corriger » et « Ouvrir la
// demande » mènent à l'écran de correction, comme dans le panneau, et « Voir
// la liste » à la liste filtrée d'une information qui porte son propre lien
// (élèves ou personnel sans photo ; navigation seule).
//
// Gravité « campagne » (À COMPLÉTER) : ton gris neutre, jamais ambre ni
// rouge. Le serveur écarte ces détecteurs du délai moyen, des nouvelles sur 7
// jours, de « À traiter » et de « En attente » ; leurs lignes figurent dans les
// alertes en cours et les résolues. Gravité « surveillance » (À SURVEILLER,
// classes presque pleines, 30/10/2026) : même ton et même régime, mais ses
// lignes n'entrent jamais dans les résolues (une classe qui n'est plus presque
// pleine n'a pas été corrigée : le serveur la résout sans résolution).
//
// Une vérification (détecteur) en échec ne rend aucune ligne : la page ne le
// prend jamais pour « rien à signaler ». La carte des alertes en cours dit
// alors que l'état n'est pas connu, et les tuiles en cours sont « incomplètes ».
//
// Sans les tables de suivi (migration du 13/10/2026 pas encore passée), le
// serveur rend les alertes en cours calculées en direct et un historique
// vide : un bandeau ambre le dit, et les chiffres d'historique s'affichent
// « - », jamais un 0 qu'on prendrait pour l'état réel.
//
// Les données sont tenues par la page (useSuiviAlertes), pas par l'onglet :
// Radix démonte l'onglet qu'on quitte, et revenir au suivi ne doit pas tout
// relire. La lecture part dès l'ouverture de la page, avec celle des réglages.
//
// Tons et textes des détecteurs : ceux du panneau
// (PanneauAlertesCritiques.jsx), recopiés ici parce qu'il ne les exporte pas.
// Une retouche là-bas se reporte ici. Pas d'icône de détecteur (le titre du
// groupe et son badge de gravité suffisent), ni sur les boutons de ligne.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Info,
  Lock,
  RefreshCw
} from 'lucide-react';
import { Button } from '../ui/button';
import EnTeteSection from '../parametrage/EnTeteSection';
import CarteReglages from '../parametrage/CarteReglages';
import ChargementSection from '../parametrage/ChargementSection';
import { formaterNombre } from '../../utils/montants';
import { lireSuivi } from './apiAlertesCritiques';
import { anneeDeSession, horsSession } from './useAlertesCritiques';
import { BADGE_NOMBRE, montantsInsecables } from './PiecesAlertes';

export const ACCENT_SUIVI = '#7c3aed';
const ACCENT_SUIVI_SOMBRE = '#6d28d9';

// Les deux séries de l'avancement : les nouvelles à l'accent de l'onglet, les
// résolues en teal, la couleur des étapes franchies (DESIGN-SYSTEM.md).
const COULEUR_NOUVELLES = '#8b5cf6';
const COULEUR_RESOLUES = '#319190';

// Exporté : le bandeau d'échec des réglages, sur la même page, prend le même.
export const BOUTON_REESSAYER = 'mt-3 inline-flex h-9 items-center gap-2 rounded-lg border border-red-300 bg-white px-4 text-sm font-semibold text-red-800 shadow-sm transition-colors hover:bg-red-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/40 disabled:cursor-not-allowed disabled:opacity-50';
const BOUTON_DEPLIER = 'inline-flex h-8 items-center gap-2 rounded-lg px-3 text-xs font-semibold text-[#246990] transition-colors hover:bg-[#eef7fb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/40';
// Les actions d'une ligne, comme dans le panneau (PanneauAlertesCritiques.jsx) :
// rounded-lg et anneau #246990 remplacent le rounded-md de la taille sm.
const BOUTON_LIGNE = 'h-8 w-full justify-center gap-2';
const BOUTON_PLEIN = 'rounded-lg bg-[#246990] text-white hover:bg-[#1f5a7a] focus-visible:ring-2 focus-visible:ring-[#246990]/40';
// Secondaire écrit en clair, comme dans le panneau : la variante outline de
// ui/button sort transparente (variables CSS absentes de KLASYS).
const BOUTON_SECONDAIRE = 'rounded-lg border border-gray-300 bg-white text-gray-700 shadow-sm '
  + 'hover:bg-gray-50 hover:text-gray-900 focus-visible:ring-2 focus-visible:ring-[#246990]/40';
// Une consigne à la place d'un bouton (autre année, constat réservé au panneau).
const CONSIGNE_LIGNE = 'inline-flex min-h-8 w-full items-center justify-center rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-center text-xs font-medium text-gray-600';

// Au-delà, un détecteur replie ses lignes, comme dans le panneau.
const LIGNES_VISIBLES = 20;
const RESOLUES_VISIBLES = 15;

const TONS = {
  critique: {
    libelle: 'Critique',
    cadre: 'border-red-200',
    entete: 'bg-red-50',
    titre: 'text-gray-900',
    badge: 'bg-red-100 text-red-700',
    texte: 'text-red-700'
  },
  importante: {
    libelle: 'Importante',
    cadre: 'border-amber-200',
    entete: 'bg-amber-50',
    titre: 'text-gray-900',
    badge: 'bg-amber-100 text-amber-800',
    texte: 'text-amber-800'
  },
  // Gris neutre, jamais ambre ni rouge : le manque de photo n'est pas une faute.
  campagne: {
    libelle: 'À compléter',
    cadre: 'border-gray-200',
    entete: 'bg-gray-50',
    titre: 'text-gray-800',
    badge: 'bg-gray-200 text-gray-700',
    texte: 'text-gray-600'
  },
  // Le même gris neutre : une classe presque pleine n'est ni une faute ni un manque.
  surveillance: {
    libelle: 'À surveiller',
    cadre: 'border-gray-200',
    entete: 'bg-gray-50',
    titre: 'text-gray-800',
    badge: 'bg-gray-200 text-gray-700',
    texte: 'text-gray-600'
  }
};

const STATUTS = {
  a_traiter: { libelle: 'À traiter', classes: 'border-red-200 bg-red-50 text-red-700' },
  en_attente: { libelle: 'En attente de validation', classes: 'border-amber-200 bg-amber-50 text-amber-800' },
  information: { libelle: 'Pour information', classes: 'border-gray-200 bg-gray-50 text-gray-600' }
};

// Le statut vient du serveur ; à défaut, celui qu'il déduit de l'action.
const STATUT_PAR_ACTION = {
  corriger: 'a_traiter',
  valider: 'a_traiter',
  constater: 'a_traiter',
  attendre_validation: 'en_attente',
  information: 'information'
};

const RESOLUTIONS = {
  corrigee: { libelle: 'Corrigée', classes: 'border-[#319190]/30 bg-[#319190]/10 text-[#2a7a79]' },
  constatee: { libelle: 'Constatée', classes: 'border-[#246990]/20 bg-[#eef7fb] text-[#246990]' }
};

// ============================================================================
// Mise en forme
// ============================================================================

const pluriel = (nombre, un, plusieurs) => (nombre > 1 ? plusieurs : un);

const estObjet = (valeur) => Boolean(valeur) && typeof valeur === 'object' && !Array.isArray(valeur);

/** Un nombre, ou null si la valeur est absente ou illisible (jamais 0 par défaut). */
const nombreOuNull = (valeur) => {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const n = Number(valeur);
  return Number.isFinite(n) ? n : null;
};

/** « 1 250 », ou « - » quand le serveur n'a rien donné. */
const compte = (valeur) => {
  const n = nombreOuNull(valeur);
  return n === null ? '-' : formaterNombre(n);
};

const statutDe = (ligne) => (STATUTS[ligne.statut] ? ligne.statut : STATUT_PAR_ACTION[ligne.action] || 'information');

const majuscule = (texte) => (texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte);

/** « Depuis 3 jours », « Depuis aujourd'hui » ; null si l'ancienneté est inconnue. */
const depuis = (jours) => {
  const n = nombreOuNull(jours);
  if (n === null) return null;
  if (n <= 0) return "Depuis aujourd'hui";
  return `Depuis ${formaterNombre(n)} ${pluriel(n, 'jour', 'jours')}`;
};

/** « en 3 jours », « le jour même ». */
const enCombienDeJours = (jours) => {
  const n = nombreOuNull(jours);
  if (n === null) return '-';
  if (n <= 0) return 'le jour même';
  return `en ${formaterNombre(n)} ${pluriel(n, 'jour', 'jours')}`;
};

const FORMAT_DECIMAL = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/** Le délai moyen, en valeur et unité séparées pour la tuile. */
const delaiMoyen = (jours) => {
  const n = nombreOuNull(jours);
  if (n === null) return { valeur: '-', unite: null };
  if (n < 1) return { valeur: '< 1', unite: 'jour' };
  return { valeur: FORMAT_DECIMAL.format(n), unite: n >= 2 ? 'jours' : 'jour' };
};

// Les DATETIME du serveur sont à l'heure UTC : horloge MySQL en UTC (relevé du
// 13/10/2026) et pilote réglé sur '+00:00'. Un instant ISO complet (…Z) se
// relit tel quel ; une chaîne sans fuseau est lue comme UTC ; une date seule
// s'écrit en UTC, sans glisser d'un jour selon le fuseau du poste.
//
// Deux usages. Une heure (dernier affichage, date des chiffres dans un message
// d'échec) se montre à
// l'heure du poste. Un jour posé à côté d'un calcul du serveur (ancienneté,
// durée, barres de l'avancement, tous comptés en jours UTC) s'écrit en jour
// UTC (jourServeur) : sinon une détection à 23 h 30 UTC afficherait
// « Depuis 1 jour - le 13 oct. » sur un poste à UTC+1, et une résolution
// tomberait dans la barre du 12 mais s'écrirait le 13 dans le tableau.
const MOTIF_FUSEAU = /(Z|[+-]\d{2}:?\d{2})$/;
const MOTIF_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/;

function lireInstant(valeur) {
  const texte = String(valeur || '').trim();
  if (!texte) return null;
  const m = MOTIF_DATE.exec(texte);
  if (!m) return null;
  const date = MOTIF_FUSEAU.test(texte) && m[4] !== undefined
    ? new Date(texte)
    : new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0)));
  if (Number.isNaN(date.getTime())) return null;
  return { date, heure: m[4] !== undefined };
}

const formatDate = (heure) => new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric', month: 'short', year: 'numeric', ...(heure ? {} : { timeZone: 'UTC' })
});
const FORMAT_HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

/** « 13 oct. 2026 à 18:10 », à l'heure du poste ; la date seule si la valeur n'a pas d'heure. */
const dateHeure = (valeur) => {
  const lu = lireInstant(valeur);
  if (!lu) return null;
  const jour = formatDate(lu.heure).format(lu.date);
  return lu.heure ? `${jour} à ${FORMAT_HEURE.format(lu.date)}` : jour;
};

const FORMAT_JOUR_SERVEUR = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'
});

/** « 13 oct. 2026 », le jour du serveur (UTC) : celui de ses calculs en jours. */
const jourServeur = (valeur) => {
  const lu = lireInstant(valeur);
  return lu ? FORMAT_JOUR_SERVEUR.format(lu.date) : null;
};

const FORMAT_JOUR_PARIS = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris'
});
const FORMAT_HEURE_PARIS = new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris'
});

/**
 * « 16/10/2026 à 10:46 », à l'heure de Paris quel que soit le poste : le
 * début du suivi se dit comme on l'a vécu à l'école. Null sans heure.
 */
const dateHeureParis = (valeur) => {
  const lu = lireInstant(valeur);
  if (!lu || !lu.heure) return null;
  return `${FORMAT_JOUR_PARIS.format(lu.date)} à ${FORMAT_HEURE_PARIS.format(lu.date)}`;
};

const FORMAT_JOUR_SEMAINE = new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' });

/** Un jour de l'avancement (`AAAA-MM-JJ`), lu en UTC. */
function lireJour(jour) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(jour || ''));
  if (!m) return null;
  return { date: new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))), numero: Number(m[3]) };
}

// ============================================================================
// Lecture
// ============================================================================

const INCOMPLETE = 'Réponse du serveur incomplète.';

/**
 * La réponse du serveur, vérifiée. Une réponse incomplète est un échec de
 * lecture, jamais complétée par des zéros présentés comme l'état réel
 * (PARAMETRAGE-MODULES.md §6). Seul l'historique peut manquer, quand la
 * migration n'est pas passée : il est alors vide.
 */
function preparerSuivi(reponse) {
  if (!estObjet(reponse) || !estObjet(reponse.compteurs) || !Array.isArray(reponse.en_cours)) {
    throw new Error(INCOMPLETE);
  }
  const migrationManquante = Boolean(reponse.migrationManquante);
  const historique = (champ) => {
    if (Array.isArray(reponse[champ])) return reponse[champ];
    if (migrationManquante) return [];
    throw new Error(INCOMPLETE);
  };
  return {
    genereLe: reponse.genere_le || null,
    migrationManquante,
    // Le début du suivi (MIN de la première détection, en base) ; absent d'un
    // serveur plus ancien ou d'un suivi vide : pas de bandeau.
    debutSuivi: typeof reponse.debut_suivi === 'string' ? reponse.debut_suivi : null,
    compteurs: reponse.compteurs,
    enCours: reponse.en_cours.filter((detecteur) => estObjet(detecteur)
      && Array.isArray(detecteur.lignes) && detecteur.lignes.length > 0),
    resolues: historique('resolues'),
    avancement: historique('avancement'),
    relances: historique('relances'),
    erreurs: Array.isArray(reponse.erreurs) ? reponse.erreurs : []
  };
}

/**
 * L'état du suivi, tenu par la page des paramètres. Une lecture plus récente
 * l'emporte sur une réponse en retard. Un échec d'actualisation garde les
 * données déjà affichées : le bandeau dit de quand elles datent.
 *
 * @returns {{ donnees: object|null, chargement: boolean, erreur: string|null,
 *   refus: boolean, charger: () => Promise<void> }}
 */
export function useSuiviAlertes() {
  const [etat, setEtat] = useState({ donnees: null, chargement: true, erreur: null, refus: false });
  const numeroRef = useRef(0);
  const monteRef = useRef(false);

  useEffect(() => {
    monteRef.current = true;
    return () => {
      monteRef.current = false;
    };
  }, []);

  const charger = useCallback(async () => {
    numeroRef.current += 1;
    const numero = numeroRef.current;
    const aJour = () => monteRef.current && numero === numeroRef.current;
    setEtat((precedent) => ({ ...precedent, chargement: true }));
    try {
      const reponse = await lireSuivi();
      if (reponse?.success === false) throw new Error(reponse.message || "Le suivi des alertes n'a pas pu être lu.");
      const donnees = preparerSuivi(reponse);
      if (!aJour()) return;
      setEtat({ donnees, chargement: false, erreur: null, refus: false });
    } catch (e) {
      if (!aJour()) return;
      // Lecture refusée : un droit qui manque, pas une panne à réessayer.
      if (e?.statut === 403) {
        setEtat({ donnees: null, chargement: false, erreur: null, refus: true });
        return;
      }
      setEtat((precedent) => ({
        ...precedent,
        chargement: false,
        erreur: e?.message || "Le suivi des alertes n'a pas pu être lu.",
        refus: false
      }));
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  return { ...etat, charger };
}

// ============================================================================
// Bandeau de chiffres
// ============================================================================

function Tuile({ libelle, valeur, unite, note, couleur, alerte }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-gray-200/80 bg-white py-3.5 pl-5 pr-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <span className="absolute inset-y-3 left-0 w-1 rounded-r" style={{ backgroundColor: couleur }} aria-hidden="true" />
      <p className="text-xs text-gray-500">{libelle}</p>
      {/* Six valeurs côte à côte : font-semibold, le gras fort est réservé aux
          titres et à un chiffre unique (PARAMETRAGE-MODULES.md §10). */}
      <p className={`mt-1 flex items-baseline gap-1.5 text-2xl font-semibold tabular-nums ${alerte ? 'text-red-700' : 'text-gray-900'}`}>
        {valeur}
        {unite && <span className="text-xs font-semibold text-gray-500">{unite}</span>}
      </p>
      {note && <p className="mt-0.5 text-[11px] leading-snug text-gray-400">{note}</p>}
    </div>
  );
}

function BandeauChiffres({ compteurs, historique, echecs }) {
  const aTraiter = nombreOuNull(compteurs.a_traiter);
  const information = nombreOuNull(compteurs.information);
  const delai = delaiMoyen(historique ? compteurs.delai_moyen_jours : null);
  const sansHistorique = 'historique non installé';
  // Un détecteur en échec ne compte aucune ligne : les chiffres en cours sont
  // alors un minimum, jamais présentés comme l'état réel.
  const incomplet = echecs > 0
    ? `incomplet - ${echecs > 1 ? `${echecs} vérifications ont échoué` : 'une vérification a échoué'}`
    : null;

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
      <Tuile
        libelle="À traiter"
        valeur={compte(aTraiter)}
        alerte={aTraiter > 0}
        couleur="#dc2626"
        note={incomplet || (information > 0
          ? `+ ${formaterNombre(information)} pour information`
          : 'corrections et validations attendues')}
      />
      <Tuile
        libelle="En attente de validation"
        valeur={compte(compteurs.en_attente)}
        couleur="#d97706"
        note={incomplet || 'corrections envoyées, pas encore validées'}
      />
      <Tuile
        libelle="Résolues sur 30 jours"
        valeur={historique ? compte(compteurs.resolues_30j) : '-'}
        couleur={COULEUR_RESOLUES}
        note={historique ? 'corrigées ou constatées' : sansHistorique}
      />
      <Tuile
        libelle="Constatées sur 30 jours"
        valeur={historique ? compte(compteurs.constatees_30j) : '-'}
        couleur="#246990"
        note={historique ? 'laissées telles quelles, avec un motif' : sansHistorique}
      />
      <Tuile
        libelle="Nouvelles sur 7 jours"
        valeur={historique ? compte(compteurs.nouvelles_7j) : '-'}
        couleur={COULEUR_NOUVELLES}
        note={historique ? 'détectées dans la semaine' : sansHistorique}
      />
      <Tuile
        libelle="Délai moyen de résolution"
        valeur={delai.valeur}
        unite={delai.unite}
        couleur="#9ca3af"
        note={historique ? 'de la détection à la résolution' : sansHistorique}
      />
    </div>
  );
}

// ============================================================================
// Avancement sur 30 jours
// ============================================================================

const Pastille = ({ couleur, libelle }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: couleur }} aria-hidden="true" />
    {libelle}
  </span>
);

const texteJour = (jour) => {
  const morceaux = [
    `${formaterNombre(jour.nouvelles)} ${pluriel(jour.nouvelles, 'nouvelle', 'nouvelles')}`,
    `${formaterNombre(jour.resolues)} ${pluriel(jour.resolues, 'résolue', 'résolues')}`
  ];
  if (jour.ouvertes !== null) {
    morceaux.push(`${formaterNombre(jour.ouvertes)} ${pluriel(jour.ouvertes, 'ouverte', 'ouvertes')}`);
  }
  return `${majuscule(FORMAT_JOUR_SEMAINE.format(jour.lu.date))} : ${morceaux.join(', ')}`;
};

function CarteAvancement({ avancement, compteurs, historique }) {
  const [survole, setSurvole] = useState(null);

  const jours = useMemo(() => avancement
    .map((jour) => ({
      cle: String(jour?.jour || ''),
      lu: lireJour(jour?.jour),
      nouvelles: nombreOuNull(jour?.nouvelles) ?? 0,
      resolues: nombreOuNull(jour?.resolues) ?? 0,
      ouvertes: nombreOuNull(jour?.ouvertes)
    }))
    .filter((jour) => jour.lu), [avancement]);

  if (!historique) {
    return (
      <CarteReglages titre="Avancement sur 30 jours" accent={ACCENT_SUIVI}>
        <p className="text-xs text-gray-400">
          L'avancement apparaîtra une fois l'historique des alertes installé dans l'école.
        </p>
      </CarteReglages>
    );
  }

  const resolues = nombreOuNull(compteurs.resolues_30j) ?? 0;
  const ouvertes = (nombreOuNull(compteurs.a_traiter) ?? 0)
    + (nombreOuNull(compteurs.en_attente) ?? 0)
    + (nombreOuNull(compteurs.information) ?? 0);
  const total = resolues + ouvertes;
  const part = total > 0 ? Math.round((resolues / total) * 100) : null;

  const maximum = Math.max(1, ...jours.map((jour) => Math.max(jour.nouvelles, jour.resolues)));
  const hauteur = (valeur) => (valeur > 0 ? `${Math.max(4, (valeur / maximum) * 100)}%` : '0');
  const indexAffiche = survole !== null && survole < jours.length ? survole : jours.length - 1;
  const jourAffiche = jours[indexAffiche] || null;

  return (
    <CarteReglages
      titre="Avancement sur 30 jours"
      accent={ACCENT_SUIVI}
      compteur={part !== null ? `${part} % réglées` : undefined}
    >
      <div className="space-y-5">
        <div>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <p className="text-sm font-semibold text-gray-800">Alertes réglées</p>
            <p className="text-xs text-gray-500">
              {total > 0
                ? `${formaterNombre(resolues)} ${pluriel(resolues, 'résolue', 'résolues')} - ${formaterNombre(ouvertes)} encore ${pluriel(ouvertes, 'ouverte', 'ouvertes')}`
                : 'Aucune alerte sur 30 jours'}
            </p>
          </div>
          <div
            className="mt-2 h-2.5 overflow-hidden rounded-md bg-gray-100"
            role="progressbar"
            aria-label="Part des alertes réglées sur 30 jours"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={part ?? 0}
          >
            <div className="h-full rounded-md transition-all" style={{ width: `${part ?? 0}%`, backgroundColor: COULEUR_RESOLUES }} />
          </div>
          <p className="mt-1.5 text-[11px] text-gray-400">
            Résolues sur 30 jours, rapportées aux résolues et aux alertes encore ouvertes.
          </p>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Jour par jour</p>
            <div className="flex items-center gap-3 text-[11px] text-gray-500">
              <Pastille couleur={COULEUR_NOUVELLES} libelle="Nouvelles" />
              <Pastille couleur={COULEUR_RESOLUES} libelle="Résolues" />
            </div>
          </div>

          {jours.length === 0 ? (
            <p className="mt-3 text-xs text-gray-400">Aucun mouvement enregistré sur 30 jours.</p>
          ) : (
            <>
              <p className="mt-2 min-h-[1rem] text-xs text-gray-600" aria-live="polite">
                {jourAffiche ? texteJour(jourAffiche) : null}
              </p>
              <div
                className="mt-2 flex h-28 items-end gap-[3px] border-b border-gray-200"
                role="list"
                aria-label="Alertes nouvelles et résolues, jour par jour"
                onMouseLeave={() => setSurvole(null)}
              >
                {jours.map((jour, index) => (
                  <div
                    key={jour.cle}
                    role="listitem"
                    tabIndex={0}
                    aria-label={texteJour(jour)}
                    onMouseEnter={() => setSurvole(index)}
                    onFocus={() => setSurvole(index)}
                    onBlur={() => setSurvole(null)}
                    className={`flex h-full min-w-0 flex-1 items-end justify-center gap-px rounded-t-sm outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#7c3aed]/40 ${index === indexAffiche ? 'bg-gray-100' : ''
                      }`}
                  >
                    <span className="w-[42%] rounded-t-sm" style={{ height: hauteur(jour.nouvelles), backgroundColor: COULEUR_NOUVELLES }} />
                    <span className="w-[42%] rounded-t-sm" style={{ height: hauteur(jour.resolues), backgroundColor: COULEUR_RESOLUES }} />
                  </div>
                ))}
              </div>
              <div className="mt-1 flex gap-[3px]" aria-hidden="true">
                {jours.map((jour, index) => (
                  <span key={jour.cle} className="min-w-0 flex-1 text-center text-[10px] tabular-nums text-gray-400">
                    {/* Un repère tous les cinq jours, compté depuis aujourd'hui. */}
                    {(jours.length - 1 - index) % 5 === 0 ? jour.lu.numero : ''}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </CarteReglages>
  );
}

// ============================================================================
// Relances des agents
// ============================================================================

const ENTETE_TABLEAU = 'py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500';

function CarteRelances({ relances, historique }) {
  return (
    <CarteReglages
      titre="Relances des agents"
      accent={ACCENT_SUIVI}
      compteur={historique && relances.length
        ? `${relances.length} ${pluriel(relances.length, 'agent', 'agents')} - 30 jours`
        : undefined}
      dense
    >
      {!historique && (
        <p className="px-5 py-6 text-xs text-gray-400">
          Les relances apparaîtront une fois l'historique des alertes installé dans l'école.
        </p>
      )}
      {historique && relances.length === 0 && (
        <p className="px-5 py-6 text-xs text-gray-400">
          Aucun geste d'agent sur 30 jours : le panneau central ne s'est ouvert pour personne, personne
          n'a cliqué « Corriger » dans le panneau central ou dans le panneau « Alertes » à droite, et
          aucune alerte réglée n'a d'auteur connu.
        </p>
      )}
      {historique && relances.length > 0 && (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px] text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th scope="col" className={`px-5 ${ENTETE_TABLEAU}`}>Agent</th>
                  <th scope="col" className={`px-2 text-right ${ENTETE_TABLEAU}`}>Affichages</th>
                  <th scope="col" className={`px-2 text-right ${ENTETE_TABLEAU}`}>Rappels</th>
                  <th scope="col" className={`px-2 text-right ${ENTETE_TABLEAU}`}>Clics Corriger</th>
                  <th scope="col" className={`px-2 text-right ${ENTETE_TABLEAU}`}>Alertes réglées</th>
                  <th scope="col" className={`px-2 text-right ${ENTETE_TABLEAU}`}>Constats</th>
                  <th scope="col" className={`px-5 text-right ${ENTETE_TABLEAU}`}>Dernier affichage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {/* Des comptes, sans verdict : un clic ne prouve pas une
                    correction, et une correction faite sans demande (classe,
                    photo) n'a pas d'auteur. Un agent qui a réglé des alertes
                    sans toucher au panneau a sa ligne, gestes à 0. */}
                {relances.map((agent, index) => (
                  <tr key={agent.employe_id ?? `agent-${index}`} className="transition-colors hover:bg-gray-50/70">
                    <td className="px-5 py-3 text-sm font-semibold text-gray-800">
                      {agent.nom || `Employé n° ${agent.employe_id}`}
                    </td>
                    <td className="px-2 py-3 text-right text-sm tabular-nums text-gray-700">{compte(agent.affichages)}</td>
                    <td className="px-2 py-3 text-right text-sm tabular-nums text-gray-700">{compte(agent.rappels)}</td>
                    <td className="px-2 py-3 text-right text-sm tabular-nums text-gray-700">{compte(agent.corrections)}</td>
                    <td className="px-2 py-3 text-right text-sm tabular-nums text-gray-700">{compte(agent.alertes_reglees)}</td>
                    <td className="px-2 py-3 text-right text-sm tabular-nums text-gray-700">{compte(agent.constats)}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-right text-xs text-gray-500">
                      {dateHeure(agent.dernier_affichage) || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-gray-100 px-5 py-3 text-[11px] leading-relaxed text-gray-400">
            Affichages et dernier affichage : le seul panneau central, celui qui s'ouvre au milieu de
            l'écran ; tant qu'un agent garde ouvert le panneau « Alertes » à droite, le panneau central est
            suspendu : ses affichages baissent, sans qu'il ait cessé de suivre les alertes. Rappels :
            panneau central repoussé par « Me rappeler ». Clics Corriger : clics sur
            « Corriger », « Ouvrir la demande » ou « Voir la liste » dans le panneau central ou dans le
            panneau « Alertes » à droite (une fois par ligne à chaque ouverture de celui-ci) ; un clic ne
            prouve pas qu'une alerte a été réglée. Alertes réglées : alertes corrigées par une demande de
            cet agent, validée au Centre de Support. Une correction faite sans demande (une classe, une
            photo) n'a pas d'auteur connu et n'est pas comptée ici.
          </p>
        </div>
      )}
    </CarteReglages>
  );
}

// ============================================================================
// Alertes en cours : le panneau en page
// ============================================================================

function LigneSuivi({ ligne, lienDetecteur, anneeSession, onCorriger }) {
  const lien = ligne.lien || lienDetecteur || null;
  const autreAnnee = ligne.annee_scolaire_id != null && anneeSession != null
    && String(ligne.annee_scolaire_id) !== String(anneeSession);
  // L'écran de correction refuserait un élève d'une autre année que la session.
  const aCorrigerAilleurs = horsSession(ligne, anneeSession);
  const cleStatut = statutDe(ligne);
  const statut = STATUTS[cleStatut];
  // Une ligne à traiter qui ne se corrige ni ne se valide : un frais encaissé,
  // à constater. Le serveur la donne en 'constater' à un validateur et en
  // 'information' aux autres, avec le même statut : la consigne vaut pour
  // les deux, quel que soit l'administrateur qui regarde.
  const aConstater = cleStatut === 'a_traiter' && !['corriger', 'valider'].includes(ligne.action);
  // Le lien propre de la ligne, jamais celui du détecteur : les informations
  // d'avant (site distant, frais encaissé) restent sans bouton.
  const lienListe = ligne.action === 'information' && ligne.lien ? ligne.lien : null;
  const anciennete = depuis(ligne.age_jours);
  const detecteeLe = jourServeur(ligne.premiere_detection);

  return (
    <li className="px-4 py-3">
      <div className="grid gap-3 lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_11rem_11rem] lg:items-start">
        <div className="min-w-0">
          <p className="break-words text-sm font-semibold text-gray-900">{ligne.libelle}</p>
          {ligne.precision && <p className="break-words text-xs text-gray-500">{montantsInsecables(ligne.precision)}</p>}
          {autreAnnee && (
            <span className="mt-1 inline-flex h-6 items-center rounded-lg bg-gray-100 px-2 text-[11px] font-semibold text-gray-600">
              Année {ligne.annee_nom || ligne.annee_scolaire_id}
            </span>
          )}
        </div>

        <div className="min-w-0 space-y-0.5">
          <p className="break-words text-sm text-gray-700">{montantsInsecables(ligne.constat)}</p>
          {ligne.correction && <p className="break-words text-sm font-semibold text-gray-900">{montantsInsecables(ligne.correction)}</p>}
          {ligne.impact && <p className="break-words text-xs text-gray-500">{montantsInsecables(ligne.impact)}</p>}
        </div>

        <div className="min-w-0 space-y-1">
          <span className={`inline-flex h-6 w-44 max-w-full items-center justify-center whitespace-nowrap rounded-lg border px-2 text-[11px] font-semibold ${statut.classes}`}>
            {statut.libelle}
          </span>
          {anciennete && (
            <p className="text-[11px] text-gray-500">
              {anciennete}
              {detecteeLe && <span className="text-gray-400"> - le {detecteeLe}</span>}
            </p>
          )}
          {ligne.auteur_nom && <p className="break-words text-[11px] text-gray-500">Saisi par {ligne.auteur_nom}</p>}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          {aCorrigerAilleurs && (
            <span className={CONSIGNE_LIGNE}>
              À corriger dans une session ouverte sur l'année {ligne.annee_nom || ligne.annee_scolaire_id}
            </span>
          )}
          {ligne.action === 'corriger' && !aCorrigerAilleurs && lien && (
            <Button type="button" size="sm" className={`${BOUTON_LIGNE} ${BOUTON_PLEIN}`} onClick={() => onCorriger(lien)}>
              Corriger
            </Button>
          )}
          {ligne.action === 'valider' && lien && (
            <Button type="button" size="sm" className={`${BOUTON_LIGNE} ${BOUTON_PLEIN}`} onClick={() => onCorriger(lien)}>
              Ouvrir la demande
            </Button>
          )}
          {/* Rien ne s'enregistre ici : le constat reste un geste du panneau. */}
          {aConstater && (
            <span className={CONSIGNE_LIGNE}>À constater par un validateur, depuis le panneau</span>
          )}
          {lienListe && (
            <Button type="button" size="sm" className={`${BOUTON_LIGNE} ${BOUTON_SECONDAIRE}`} onClick={() => onCorriger(lienListe)}>
              Voir la liste
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

function GroupeSuivi({ detecteur, deplie, onDeplier, anneeSession, onCorriger }) {
  const ton = TONS[detecteur.gravite] || TONS.importante;
  const lignes = detecteur.lignes || [];
  const total = Number.isFinite(Number(detecteur.total)) ? Number(detecteur.total) : lignes.length;
  const visibles = deplie ? lignes : lignes.slice(0, LIGNES_VISIBLES);
  const reste = lignes.length - visibles.length;

  return (
    <section className={`overflow-hidden rounded-lg border ${ton.cadre}`}>
      <header className={`px-4 py-3 ${ton.entete}`}>
        {/* Le nombre dans sa colonne fixe, au bord droit, comme dans les panneaux (31/10/2026). */}
        <div className="flex items-start gap-2">
          <div className="flex min-h-6 min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5">
            <h3 className={`text-sm font-semibold ${ton.titre}`}>{detecteur.titre}</h3>
            <span className={`text-[11px] font-semibold uppercase tracking-wide ${ton.texte}`}>{ton.libelle}</span>
          </div>
          <span className={`${BADGE_NOMBRE} ${ton.badge}`}>{total}</span>
        </div>
        {detecteur.description && <p className="mt-0.5 text-xs text-gray-600">{detecteur.description}</p>}
      </header>

      <ul className="divide-y divide-gray-100 bg-white">
        {visibles.map((ligne, index) => (
          <LigneSuivi
            key={ligne.cle ?? `ligne-${index}`}
            ligne={ligne}
            lienDetecteur={detecteur.lien}
            anneeSession={anneeSession}
            onCorriger={onCorriger}
          />
        ))}
      </ul>

      {reste > 0 && (
        <div className="border-t border-gray-100 bg-white px-4 py-2">
          <button type="button" className={BOUTON_DEPLIER} onClick={onDeplier}>
            <ChevronDown className="h-4 w-4" />
            Afficher {pluriel(reste, 'l\'autre ligne', `les ${reste} autres lignes`)}
          </button>
        </div>
      )}
    </section>
  );
}

function CarteEnCours({ enCours, erreurs, anneeSession, onCorriger }) {
  const [deplies, setDeplies] = useState({});
  const total = enCours.reduce(
    (somme, detecteur) => somme + (Number.isFinite(Number(detecteur.total)) ? Number(detecteur.total) : detecteur.lignes.length),
    0
  );

  // Un détecteur en échec ne rend aucune ligne : sans ligne ni échec, tout est
  // réglé ; sans ligne mais avec un échec, l'état n'est pas connu.
  const echec = erreurs.length > 0;
  const toutRegle = enCours.length === 0 && !echec;
  const inconnu = enCours.length === 0 && echec;

  return (
    <CarteReglages
      titre="Alertes en cours"
      accent={ACCENT_SUIVI}
      compteur={inconnu ? undefined : `${formaterNombre(total)} ${pluriel(total, 'ligne', 'lignes')}`}
    >
      <div className="space-y-4">
        {toutRegle && (
          <div className="flex items-center gap-3 rounded-lg border border-[#319190]/30 bg-[#319190]/10 p-4">
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#2a7a79]" />
            <p className="text-sm text-[#2a7a79]">Aucune alerte en cours : rien n'attend dans le panneau.</p>
          </div>
        )}

        {inconnu && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              {erreurs.length > 1
                ? `Aucune alerte trouvée, mais ${erreurs.length} vérifications n'ont pas abouti : l'état n'est pas connu. Actualisez dans un moment.`
                : "Aucune alerte trouvée, mais une vérification n'a pas abouti : l'état n'est pas connu. Actualisez dans un moment."}
            </p>
          </div>
        )}

        {enCours.map((detecteur, index) => (
          <GroupeSuivi
            key={detecteur.id ?? `detecteur-${index}`}
            detecteur={detecteur}
            deplie={Boolean(deplies[detecteur.id])}
            onDeplier={() => setDeplies((precedent) => ({ ...precedent, [detecteur.id]: true }))}
            anneeSession={anneeSession}
            onCorriger={onCorriger}
          />
        ))}

        {echec && !inconnu && (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            {erreurs.length > 1
              ? `${erreurs.length} vérifications n'ont pas abouti : leurs alertes manquent ici. Actualisez dans un moment.`
              : "Une vérification n'a pas abouti : ses alertes manquent ici. Actualisez dans un moment."}
          </p>
        )}
      </div>
    </CarteReglages>
  );
}

// ============================================================================
// Résolues récemment
// ============================================================================

function CarteResolues({ resolues, compteurs, historique }) {
  const [tout, setTout] = useState(false);
  const visibles = tout ? resolues : resolues.slice(0, RESOLUES_VISIBLES);
  const reste = resolues.length - visibles.length;

  // Le total vient du compteur, comme la tuile « Résolues sur 30 jours » : la
  // liste est plafonnée par le serveur (les 200 plus récentes). Le plus grand
  // des deux, si une résolution est tombée entre les deux requêtes.
  const total = Math.max(nombreOuNull(compteurs.resolues_30j) ?? 0, resolues.length);
  let compteur;
  if (historique && resolues.length > 0) {
    compteur = resolues.length < total
      ? `les ${formaterNombre(resolues.length)} plus récentes sur ${formaterNombre(total)}`
      : `${formaterNombre(total)} sur 30 jours`;
  }

  return (
    <CarteReglages
      titre="Résolues récemment"
      accent={ACCENT_SUIVI}
      compteur={compteur}
      dense
    >
      {!historique && (
        <p className="px-5 py-6 text-xs text-gray-400">
          Les alertes résolues apparaîtront une fois l'historique des alertes installé dans l'école.
        </p>
      )}
      {historique && resolues.length === 0 && (
        <p className="px-5 py-6 text-xs text-gray-400">Aucune alerte résolue sur 30 jours.</p>
      )}
      {historique && resolues.length > 0 && (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/60">
                  <th scope="col" className={`px-5 ${ENTETE_TABLEAU}`}>Alerte</th>
                  <th scope="col" className={`px-3 ${ENTETE_TABLEAU}`}>Constat</th>
                  <th scope="col" className={`px-3 ${ENTETE_TABLEAU}`}>Résolution</th>
                  <th scope="col" className={`px-3 ${ENTETE_TABLEAU}`}>Par</th>
                  <th scope="col" className={`px-3 ${ENTETE_TABLEAU}`}>Durée</th>
                  <th scope="col" className={`px-5 text-right ${ENTETE_TABLEAU}`}>Résolue le</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visibles.map((alerte, index) => {
                  const resolution = RESOLUTIONS[alerte.resolution] || null;
                  return (
                    <tr
                      // Pas de clé dans le contrat : l'alerte, son instant et sa place suffisent.
                      key={`${alerte.detecteur}|${alerte.libelle}|${alerte.resolue_le}|${index}`}
                      className="align-top transition-colors hover:bg-gray-50/70"
                    >
                      <td className="px-5 py-3">
                        <p className="break-words text-sm font-semibold text-gray-800">{alerte.libelle}</p>
                        {alerte.titre && <p className="text-[11px] text-gray-400">{alerte.titre}</p>}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600">{alerte.constat || '-'}</td>
                      <td className="px-3 py-3">
                        {resolution ? (
                          <span className={`inline-flex h-6 w-24 items-center justify-center whitespace-nowrap rounded-lg border text-[11px] font-semibold ${resolution.classes}`}>
                            {resolution.libelle}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-600">{alerte.par_nom || '-'}</td>
                      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-600">{enCombienDeJours(alerte.duree_jours)}</td>
                      <td className="whitespace-nowrap px-5 py-3 text-right text-xs text-gray-500">{jourServeur(alerte.resolue_le) || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-5 py-2.5">
            <p className="text-[11px] leading-relaxed text-gray-400">
              « Par » : l'auteur du constat, ou, pour une correction, l'agent qui l'a demandée au Centre de
              Support. « - » : auteur inconnu, par exemple une correction faite sans demande (une classe,
              une photo).
            </p>
            {reste > 0 && (
              <button type="button" className={BOUTON_DEPLIER} onClick={() => setTout(true)}>
                <ChevronDown className="h-4 w-4" />
                Afficher {pluriel(reste, 'l\'autre alerte', `les ${reste} autres alertes`)}
              </button>
            )}
          </div>
        </div>
      )}
    </CarteReglages>
  );
}

// ============================================================================
// Début du suivi
// ============================================================================

/**
 * Le suivi ne reprend rien d'avant son premier passage : une alerte corrigée
 * plus tôt n'y a jamais eu de ligne (à csap1, cinq réductions corrigées le
 * matin du 16/10/2026, avant l'installation). Le bandeau le dit, avec la date
 * lue en base (le serveur rend la plus ancienne première détection), à
 * l'heure de Paris. Encadré d'explication (DESIGN-SYSTEM.md), une icône.
 */
function BandeauDebutSuivi({ debut }) {
  const quand = dateHeureParis(debut);
  if (!quand) return null;
  return (
    <div className="flex items-start gap-3 rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-4 text-xs text-gray-700">
      <Info size={16} className="mt-0.5 shrink-0 text-[#246990]" aria-hidden="true" />
      <p>Le suivi commence le {quand} : ce qui a été corrigé avant n'y figure pas.</p>
    </div>
  );
}

// ============================================================================
// L'onglet
// ============================================================================

/**
 * L'onglet « Suivi ». Lecture seule : il ne reçoit que l'état tenu par la
 * page (useSuiviAlertes) et la façon de le relire.
 */
export default function SuiviAlertesCritiques({ donnees, chargement, erreur, refus, charger }) {
  const navigate = useNavigate();
  // L'année de la session : une correction d'une autre année se fait ailleurs.
  const anneeSession = useMemo(() => anneeDeSession(), []);
  const historique = Boolean(donnees) && !donnees.migrationManquante;
  // Ni date ni « Actualiser » dans l'en-tête (décision de Daniel du 14/10/2026) :
  // le suivi se relit à chaque ouverture de la page et après un enregistrement
  // des détecteurs ou des seuils. La date ne sert plus qu'au message d'échec.
  const genereLe = donnees ? dateHeure(donnees.genereLe) : null;

  return (
    <div className="space-y-5">
      <EnTeteSection
        icone={Activity}
        titre="Suivi des alertes"
        sousTitre="Ce qui attend dans le panneau, ce qui a été réglé et par qui, et la façon dont les agents y répondent. Consultation seule."
        accent={ACCENT_SUIVI}
        accentSombre={ACCENT_SUIVI_SOMBRE}
      />

      {refus && (
        <div className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 text-xs text-gray-600">
          <Lock size={16} className="mt-0.5 shrink-0 text-gray-500" />
          <p>Le suivi des alertes est réservé aux agents habilités à gérer les alertes critiques.</p>
        </div>
      )}

      {!refus && chargement && !donnees && (
        <ChargementSection accent={ACCENT_SUIVI} libelle="Chargement du suivi…" />
      )}

      {!refus && !chargement && erreur && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-5">
          <AlertCircle size={18} className="mt-0.5 shrink-0 text-red-700" />
          <div className="min-w-0 text-sm text-red-800">
            <p className="font-semibold text-red-900">
              {donnees ? "L'actualisation du suivi a échoué" : "Le suivi des alertes n'a pas pu être lu"}
            </p>
            <p className="mt-1">
              {erreur}
              {donnees && genereLe ? ` Les chiffres affichés sont ceux du ${genereLe}.` : ''}
            </p>
            <button type="button" onClick={charger} className={BOUTON_REESSAYER}>
              <RefreshCw size={15} />
              Réessayer
            </button>
          </div>
        </div>
      )}

      {!refus && donnees && (
        <>
          {donnees.migrationManquante && (
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-amber-600" />
              <p>
                L'historique des alertes n'est pas encore installé dans cette école : seules les alertes en
                cours sont visibles. L'avancement, les relances des agents et les alertes résolues
                apparaîtront après la mise à jour. Contactez l'équipe technique.
              </p>
            </div>
          )}

          {historique && <BandeauDebutSuivi debut={donnees.debutSuivi} />}

          <BandeauChiffres compteurs={donnees.compteurs} historique={historique} echecs={donnees.erreurs.length} />

          <div className="grid gap-5 xl:grid-cols-2">
            <CarteAvancement avancement={donnees.avancement} compteurs={donnees.compteurs} historique={historique} />
            <CarteRelances relances={donnees.relances} historique={historique} />
          </div>

          <CarteEnCours
            enCours={donnees.enCours}
            erreurs={donnees.erreurs}
            anneeSession={anneeSession}
            onCorriger={(lien) => navigate(lien)}
          />

          <CarteResolues resolues={donnees.resolues} compteurs={donnees.compteurs} historique={historique} />
        </>
      )}
    </div>
  );
}
